// ✅ src/pages/VehicleV2/TyreLifeDetailReport.jsx
// UPDATED FULL FILE
//
// ✅ common localStorage / sessionStorage header pattern
// ✅ useMemo + useCallback + useRef
// ✅ pageLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ fetch once per search, paginate in UI only
// ✅ safer reset / refresh / export / print flow

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaSyncAlt,
  FaEye,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/tyreLifeDetailsReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const createDefaultFilters = (branchType, branchId) => ({
  branchId: branchType === "HO" ? "Select" : String(branchId),
  fromDate: "",
  toDate: "",
  tyreSerialNo: "",
  vehicleName: "",
  vehicleRegNo: "",
  vehicleCode: "",
  tyreModel: "",
  tyreMake: "Select",
  tyreSize: "",
  status: "Select",
});

const exportToCsv = (rows, filename = "tyre-life-details.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r[h] ?? "";
          const s = String(val).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* ✅ DB row -> UI row mapper */
const mapDbRowToUi = (r, idx) => {
  return {
    id: r.fld_tyreid ?? r.fld_addtyre_id ?? idx + 1,

    tyreSerialNo: r.fld_tyreserailno ?? r.fld_tyre_serial_no ?? "",
    tyreMake: r.fld_tyremake ?? r.fld_tyre_make ?? "",
    tyreModel: r.fld_tyremodel ?? r.fld_tyre_model ?? "",

    vehicleCode: r.fld_vehicle_code ?? "",
    vehicleNo: r.fld_vehicleregno ?? r.fld_vehicle_no ?? "",
    vehicleName: r.fld_vehicle_name ?? "",

    originalRtd: r.fld_status ?? r.fld_original_rtd ?? "",

    fittingDate: r.fld_fittingdate ?? r.fld_fitting_date ?? "",
    removingDate: r.fld_removingdate ?? r.fld_removing_date ?? "",

    fittingHrKm: toNum(r.fld_fittingkmhr ?? r.fld_fitting_hr_km, 0),
    removingHrKm: toNum(
      r.fld_RemovingKmhr ?? r.fld_removingkmhr ?? r.fld_removing_hr_km,
      0
    ),

    runningHrKm: toNum(r.fld_runninghrkm ?? r.fld_running_hr_km, 0),

    costOfTyre: toNum(r.fld_tyrerate ?? r.fld_cost_of_tyre, 0),
    costHrKm: toNum(r.fld_tyrecost ?? r.fld_cost_hr_km, 0),

    branchName: r.fld_branchname ?? "",
    tyreSize: r.fld_tyresize ?? r.fld_tyre_size ?? "",
    status: r.fld_tyrestatus ?? r.fld_currentstatus ?? r.fld_useoftyre ?? "",
    raw: r,
  };
};

export default function TyreLifeDetailReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);
  const inFlightRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || listLoading;

  /* ===========================
     ui states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     data states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [allRows, setAllRows] = useState([]);

  /* ===========================
     pagination states
  =========================== */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass || ""} text-black`
      }`,
    [isDark, currentSkin]
  );

  const tdClass = useMemo(
    () =>
      `px-4 py-4 text-sm text-center ${
        isDark ? "text-gray-300" : "text-gray-900"
      }`,
    [isDark]
  );

  const thClass = "px-4 py-4 text-center font-semibold";

  /* ===========================
     computed
  =========================== */
  const total = allRows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / itemsPerPage)),
    [total, itemsPerPage]
  );

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, total);

  const pageRows = useMemo(
    () => allRows.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage),
    [allRows, indexOfFirstItem]
  );

  const supplierGroups = useMemo(() => {
    const map = new Map();

    for (const r of pageRows) {
      const raw = r?.raw || {};
      const supplierName = toStr(raw?.fld_purchasefrom || "Unknown");
      const supplierId = toStr(raw?.fld_supplierid || supplierName);
      const key = supplierId || supplierName || "Unknown";

      if (!map.has(key)) {
        map.set(key, { supplierId, supplierName, rows: [] });
      }
      map.get(key).rows.push(r);
    }

    return Array.from(map.values());
  }, [pageRows]);

  const lastOccurrenceMap = useMemo(() => {
    const map = new Map();
    const tyreGroups = new Map();

    pageRows.forEach((row, idx) => {
      const tyreSerialNo = toStr(row.tyreSerialNo);
      if (!tyreSerialNo) return;

      if (!tyreGroups.has(tyreSerialNo)) {
        tyreGroups.set(tyreSerialNo, []);
      }
      tyreGroups.get(tyreSerialNo).push({ index: idx, row });
    });

    tyreGroups.forEach((items, tyreSerialNo) => {
      if (items.length > 0) {
        const lastItem = items[items.length - 1];
        map.set(lastItem.index, {
          isLast: true,
          tyreSerialNo,
          tyreId: lastItem.row.id,
          groupSize: items.length,
        });
      }
    });

    return map;
  }, [pageRows]);

  /* ===========================
     handlers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setField(name, value);
    },
    [setField]
  );

  /* ===========================
     api fetch
  =========================== */
  const fetchList = useCallback(
    async (overrideFilters = filters) => {
      if (inFlightRef.current) return;

      try {
        inFlightRef.current = true;
        setListLoading(true);

        const f = overrideFilters || filters;

        const params = {
          branchId:
            session.branchType === "HO"
              ? f.branchId || "Select"
              : String(session.branchId),
          fromDate: f.fromDate || "",
          toDate: f.toDate || "",
          tyreSerialNo: f.tyreSerialNo || "",
          vehicleName: f.vehicleName || "",
          vehicleRegNo: f.vehicleRegNo || "",
          vehicleCode: f.vehicleCode || "",
          tyreModel: f.tyreModel || "",
          tyreMake: f.tyreMake || "Select",
          tyreSize: f.tyreSize || "",
          status: f.status || "Select",
          page: 1,
          pageSize: 1000000,
        };

        const res = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (res.data?.success === false) {
          toast.error(res.data?.message || "Failed to load tyre life report");
          setAllRows([]);
          setCurrentPage(1);
          return;
        }

        const apiRows = toList(res.data?.rows);
        const mapped = apiRows.map(mapDbRowToUi);

        setAllRows(mapped);
        setCurrentPage(1);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "API error"
        );
        setAllRows([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
        inFlightRef.current = false;
      }
    },
    [filters, commonHeaders, session.branchType, session.branchId]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(defaultFilters);
      setAllRows([]);
      setCurrentPage(1);
      await fetchList(defaultFilters);
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, fetchList]);

  /* ===========================
     button handlers
  =========================== */
  const handleSearch = useCallback(async () => {
    await fetchList(filters);
    toast.success(`Loaded ${allRows.length || 0} records`);
  }, [fetchList, filters, allRows.length]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    await fetchList(reset);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, fetchList]);

  const handleClearFilters = useCallback(() => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    toast.info("Filters cleared. Click Search to apply.");
  }, [session.branchType, session.branchId]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const paginate = useCallback(
    (pageNumber) => {
      const next = Math.min(Math.max(1, pageNumber), totalPages);
      setCurrentPage(next);
    },
    [totalPages]
  );

  const handleTyrePrint = useCallback(
    (item, groupInfo) => {
      const tyreSerialNo = toStr(item.tyreSerialNo);
      const tyreId = toStr(item.id);

      if (!tyreSerialNo) {
        toast.error("Tyre Serial No missing");
        return;
      }

      if (!tyreId) {
        toast.error("Tyre Id missing");
        return;
      }

      toast.info(`Printing ${groupInfo.groupSize} record(s) for ${tyreSerialNo}`);

      navigate("/vehicle/TyreCostReportPage", {
        state: {
          branchType: session.branchType,
          loginUserId: String(session.loginUserId),
          sessionBranchId: Number(session.branchId),

          tyreSerialNo,
          tyreId,

          branchId: filters.branchId,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          vehicleName: filters.vehicleName,
          vehicleRegNo: filters.vehicleRegNo,
          vehicleCode: filters.vehicleCode,
          tyreModel: filters.tyreModel,
          tyreMake: filters.tyreMake,
          tyreSize: filters.tyreSize,
          status: filters.status,

          pageSize: 1000,
        },
      });
    },
    [navigate, session, filters]
  );

  const handleSupplierPrint = useCallback(
    (supplier, firstRow) => {
      const raw = firstRow?.raw || {};
      const supplierId = toStr(supplier?.supplierId);
      const supplierName = toStr(supplier?.supplierName);

      const tyreId = toStr(raw?.fld_tyreid || raw?.fld_addtyre_id || firstRow?.id);
      const tyreSerialNo = toStr(raw?.fld_tyreserailno || firstRow?.tyreSerialNo);

      if (!supplierId) return toast.error("Supplier Id missing");
      if (!tyreId) return toast.error("Tyre Id missing");
      if (!tyreSerialNo) return toast.error("Tyre Serial No missing");

      navigate("/vehicle/TyreLifeSupplierPrint", {
        state: {
          supplierId,
          supplierName,
          tyreId,
          tyreSerialNo,
        },
      });
    },
    [navigate]
  );

  const handleExport = useCallback(() => {
    try {
      if (!pageRows.length) {
        toast.info("No data to export");
        return;
      }

      const exportRows = pageRows.map((r) => ({
        tyreSerialNo: r.tyreSerialNo,
        tyreMake: r.tyreMake,
        tyreModel: r.tyreModel,
        vehicleCode: r.vehicleCode,
        vehicleNo: r.vehicleNo,
        vehicleName: r.vehicleName,
        originalRtd: r.originalRtd,
        fittingDate: r.fittingDate,
        removingDate: r.removingDate,
        fittingHrKm: r.fittingHrKm,
        removingHrKm: r.removingHrKm,
        runningHrKm: r.runningHrKm,
        costOfTyre: r.costOfTyre,
        costHrKm: r.costHrKm,
        branchName: r.branchName,
        tyreSize: r.tyreSize,
        status: r.status,
      }));

      exportToCsv(exportRows, `tyre-life-details-page-${currentPage}.csv`);
      toast.success("Exported CSV");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Export failed");
    }
  }, [pageRows, currentPage]);

  const handleAllPrint = useCallback(() => {
    toast.info("Preparing print report...");

    const currentPageRecordCount = pageRows?.length ?? 0;
    const totalRecords = allRows?.length ?? 0;

    navigate("/vehicle/TyreCostReportPage", {
      state: {
        branchType: session.branchType,
        loginUserId: String(session.loginUserId),
        sessionBranchId: Number(session.branchId),

        branchId: filters.branchId,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        tyreSerialNo: filters.tyreSerialNo,
        vehicleName: filters.vehicleName,
        vehicleRegNo: filters.vehicleRegNo,
        vehicleCode: filters.vehicleCode,
        tyreModel: filters.tyreModel,
        tyreMake: filters.tyreMake,
        tyreSize: filters.tyreSize,
        status: filters.status,

        currentPage,
        currentPageRecordCount,
        totalRecords,
        pageSize: itemsPerPage,
      },
    });
  }, [navigate, session, filters, currentPage, pageRows.length, allRows.length]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      await loadPage();
    })();
  }, [loadPage]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYRE LIFE DETAIL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-4">
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Tyre Life Detail Report
                </h1>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {total} Records
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isBusy
                      ? isDark
                        ? "bg-yellow-900 text-yellow-100"
                        : "bg-yellow-100 text-yellow-800"
                      : isDark
                      ? "bg-green-900 text-green-100"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {pageLoading ? "Loading Page" : listLoading ? "Loading List" : "Ready"}
                </span>
              </div>

              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || pageRows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || pageRows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {pageLoading ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter />
                    FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branchId"
                        value={filters.branchId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy || session.branchType !== "HO"}
                      >
                        <option value="Select">Select</option>
                        <option value={String(session.branchId)}>Branch {session.branchId}</option>
                        <option value="HILARA">HILARA</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>From Date</label>
                      <input
                        type="date"
                        name="fromDate"
                        value={filters.fromDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>To Date</label>
                      <input
                        type="date"
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Serial No</label>
                      <input
                        type="text"
                        name="tyreSerialNo"
                        value={filters.tyreSerialNo}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input
                        type="text"
                        name="vehicleName"
                        value={filters.vehicleName}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Reg No</label>
                      <input
                        type="text"
                        name="vehicleRegNo"
                        value={filters.vehicleRegNo}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input
                        type="text"
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Model</label>
                      <input
                        type="text"
                        name="tyreModel"
                        value={filters.tyreModel}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Make</label>
                      <select
                        name="tyreMake"
                        value={filters.tyreMake}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        <option value="APOLLO">APOLLO</option>
                        <option value="MRF">MRF</option>
                        <option value="CEAT">CEAT</option>
                        <option value="JK TYRES">JK TYRES</option>
                        <option value="BIRLA">BIRLA</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Tyre Size</label>
                      <input
                        type="text"
                        name="tyreSize"
                        value={filters.tyreSize}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Status</label>
                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        <option value="Active">Active</option>
                        <option value="Removed">Removed</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading ? "Loading page..." : listLoading ? "Loading list..." : "Ready"}
                    </div>

                    <div className="flex gap-4 flex-wrap">
                      <button
                        onClick={handleClearFilters}
                        type="button"
                        disabled={isBusy}
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        Clear All Filters
                      </button>

                      <button
                        onClick={handleViewAll}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        Search Records
                      </button>

                      <button
                        onClick={handleAllPrint}
                        type="button"
                        disabled={isBusy || pageRows.length === 0}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold ${
                          isBusy || pageRows.length === 0 ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaPrint />
                        Print All
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {pageRows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Tyre Records Found
                    </h3>
                    <p
                      className={`mb-6 max-w-sm mx-auto ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Try adjusting your filters or click View All.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className={thClass}>S.No</th>
                          <th className={thClass}>Tyre Serial No</th>
                          <th className={thClass}>Tyre Make</th>
                          <th className={thClass}>Tyre Model</th>
                          <th className={thClass}>Vehicle Code</th>
                          <th className={thClass}>Vehicle No</th>
                          <th className={thClass}>Original/RTD</th>
                          <th className={thClass}>Fitting Date</th>
                          <th className={thClass}>Removing Date</th>
                          <th className={thClass}>Fitting HR/KM</th>
                          <th className={thClass}>Removing HR/KM</th>
                          <th className={thClass}>Running HR/KM</th>
                          <th className={thClass}>Cost Of Tyre</th>
                          <th className={thClass}>Cost HR/KM</th>
                          <th className={thClass}>Branch Name</th>
                          <th className={thClass}>Status</th>
                          <th className={thClass}>Actions</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {pageRows.map((item, idx) => {
                          const groupInfo = lastOccurrenceMap.get(idx);
                          const showPrintButton = groupInfo?.isLast;

                          return (
                            <tr
                              key={`${item.id || idx}-${idx}`}
                              className={`${
                                isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                              } transition-colors duration-200`}
                            >
                              <td className={tdClass}>{indexOfFirstItem + idx + 1}</td>
                              <td className={tdClass}>{item.tyreSerialNo}</td>
                              <td className={tdClass}>{item.tyreMake}</td>
                              <td className={tdClass}>{item.tyreModel}</td>
                              <td className={tdClass}>{item.vehicleCode}</td>
                              <td className={tdClass}>{item.vehicleNo}</td>
                              <td className={tdClass}>{item.originalRtd}</td>
                              <td className={tdClass}>{item.fittingDate || "N/A"}</td>
                              <td className={tdClass}>{item.removingDate || "N/A"}</td>
                              <td className={tdClass}>{item.fittingHrKm ?? "N/A"}</td>
                              <td className={tdClass}>{item.removingHrKm ?? "N/A"}</td>
                              <td className={tdClass}>{item.runningHrKm ?? "N/A"}</td>
                              <td className={tdClass}>
                                ₹{Number(item.costOfTyre || 0).toLocaleString("en-IN")}
                              </td>
                              <td className={tdClass}>
                                ₹{Number(item.costHrKm || 0).toFixed(2)}
                              </td>
                              <td className={tdClass}>{item.branchName}</td>
                              <td className={`${tdClass} font-semibold`}>
                                {item.status || "N/A"}
                              </td>
                              <td className="px-4 py-4 text-center">
                                {showPrintButton ? (
                                  <button
                                    onClick={() => handleTyrePrint(item, groupInfo)}
                                    disabled={isBusy}
                                    className={`p-2 rounded ${
                                      isDark
                                        ? "bg-blue-600 text-white hover:bg-blue-700"
                                        : "bg-blue-500 text-white hover:bg-blue-600"
                                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                                    title={`Print ${groupInfo.groupSize} record(s) for ${groupInfo.tyreSerialNo}`}
                                    type="button"
                                  >
                                    <FaPrint className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {total > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
                  <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Showing {total === 0 ? 0 : indexOfFirstItem + 1} to {indexOfLastItem} of {total} entries
                  </div>

                  <nav className="flex gap-1">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1 || isBusy}
                      type="button"
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === 1 || isBusy
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          disabled={isBusy}
                          type="button"
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPage === pageNum
                              ? `${buttonClass} text-white`
                              : isDark
                              ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                              : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages || isBusy}
                      type="button"
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === totalPages || isBusy
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading ? "Loading page..." : listLoading ? "Loading list..." : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}